import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string;
}

/**
 * Composant pour protéger les routes qui nécessitent une authentification
 * Redirige vers /login si l'utilisateur n'est pas authentifié
 */
export function ProtectedRoute({
  children,
  requiredRole,
}: ProtectedRouteProps): JSX.Element {
  const location = useLocation();
  const { isAuthenticated, user } = useAuthStore();

  // Vérifier l'authentification
  if (!isAuthenticated) {
    // Rediriger vers login en sauvegardant la location d'origine
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Un client final ne doit jamais atterrir sur l'espace gestion : on le renvoie sur son portail.
  if (user?.role === "CUSTOMER") {
    return <Navigate to="/client" replace />;
  }

  // Vérifier le rôle si requis
  if (requiredRole && user?.role !== requiredRole) {
    // Rediriger vers la page d'accueil si le rôle n'est pas suffisant
    return <Navigate to="/" replace />;
  }

  // Livreur : accès limité (Estimations, Livraisons, Mon compte)
  const path = location.pathname;
  if (user?.role === "DRIVER") {
    if (path === "/" || path === "/dashboard") {
      return <Navigate to="/estimations" replace />;
    }
    const driverAllowedPrefixes = [
      "/estimations",
      "/deliveries/my",
      "/account",
      "/orders/new",
    ];
    const allowed = driverAllowedPrefixes.some(
      (a) => path === a || path.startsWith(a + "/")
    );
    if (!allowed) {
      return <Navigate to="/deliveries/my" replace />;
    }
  }

  return <>{children}</>;
}

/**
 * Hook pour vérifier l'authentification de manière asynchrone
 * (utile si on veut vérifier le token avec l'API)
 */
export function useAuthCheck(): { isAuthenticated: boolean; token: string | null } {
  const { isAuthenticated, token } = useAuthStore();
  // Pourrait faire un appel à /auth/me pour valider le token
  return { isAuthenticated, token };
}
